import { useEffect, useState } from "react"

const HEADER_HEIGHT = 56
const MARGIN = 8//24
const TOP_ADJUST = 0//-16
const MIN_TOP = HEADER_HEIGHT + MARGIN

type WidgetTopState = {
  top: number
  documentTop: number
  measured: boolean
}

export const useWidgetTop = () => {
  const [position, setPosition] = useState<WidgetTopState>({ top: MIN_TOP, documentTop: MIN_TOP, measured: false })

  useEffect(() => {
    if (typeof window === "undefined") return
    const hero = document.querySelector<HTMLElement>("[data-hero-section]")
    const container = hero?.parentElement ?? null
    if (!container) {
      const fallbackTop = MIN_TOP
      const fallbackDocumentTop = typeof window === "undefined" ? MIN_TOP : MIN_TOP + window.scrollY
      setPosition(prev => {
        if (
          prev.measured &&
          Math.abs(fallbackTop - prev.top) <= 0.5 &&
          Math.abs(fallbackDocumentTop - prev.documentTop) <= 0.5
        ) {
          return prev
        }
        return { top: fallbackTop, documentTop: fallbackDocumentTop, measured: true }
      })
      return
    }

    let raf = 0
    let ticking = false

    const syncPosition = () => {
      ticking = false
      const rect = container.getBoundingClientRect()
      const rawTop = rect.bottom + MARGIN + TOP_ADJUST
      const nextTop = Math.max(MIN_TOP, rawTop)
      const nextDocumentTop = rawTop + window.scrollY
      setPosition(prev => {
        if (
          prev.measured &&
          Math.abs(nextTop - prev.top) <= 0.5 &&
          Math.abs(nextDocumentTop - prev.documentTop) <= 0.5
        ) {
          return prev
        }
        return { top: nextTop, documentTop: nextDocumentTop, measured: true }
      })
    }

    const schedule = () => {
      if (ticking) return
      ticking = true
      raf = window.requestAnimationFrame(syncPosition)
    }

    syncPosition()
    window.addEventListener("scroll", schedule, { passive: true })
    window.addEventListener("resize", schedule)

    return () => {
      window.removeEventListener("scroll", schedule)
      window.removeEventListener("resize", schedule)
      if (raf) window.cancelAnimationFrame(raf)
    }
  }, [])

  return position
}
